import { GET, POST } from './api'
import { ReadObject, SaveObject } from './storage'
import { IDENTITY_AREA_ROUTES, IDENTITY_AREA_SESSIONS } from './identity'
import { profileAllowsArea, normalizeProfileUser, profileNeedsCompletion, profileCompletionPath, passwordChangeRequired, passwordChangePath } from './profile'
import { captureSessionState, isSessionStateCurrent, clearIdentitySessionData } from './session'

export const AREA_SELECTION_PATH = '/choose-area'
const knownArea = area => Object.hasOwn(IDENTITY_AREA_SESSIONS, area)
export const availableAreaList = areas => Array.isArray(areas)
  ? Object.keys(IDENTITY_AREA_ROUTES).filter(area => areas.includes(area)) : []
export const entryDestination = areas => availableAreaList(areas).length === 1
  ? IDENTITY_AREA_ROUTES[availableAreaList(areas)[0]] : AREA_SELECTION_PATH
const validSession = (session, userId, area) => Boolean(session?.jwt
  && session.identity?.centralUserId === userId && session.identity?.area === area
  && profileAllowsArea(session.user, area))

export const UnifiedLogin = async params => {
  clearIdentitySessionData()
  const snapshot = captureSessionState()
  const response = await POST('/auth/unified-login', params)
  if (!isSessionStateCurrent(snapshot)) return false
  if (!response?.jwt) return response
  const areas = availableAreaList(response.areas)
  const { centralUserId, area } = response.identity || {}
  if (!Number.isSafeInteger(centralUserId) || !areas.length || !areas.includes(area)
    || !validSession(response, centralUserId, area)) return false
  SaveObject(IDENTITY_AREA_SESSIONS[area], { ...response, user: normalizeProfileUser(response.user) })
  SaveObject('identity_session', { centralUserId, areas })
  return { ...response, areas }
}

export const LoadEntryAreas = async () => {
  const snapshot = captureSessionState()
  if (!snapshot.writable || !snapshot.tokens.some(Boolean)) return null
  const identity = await GET('/identity/session', true)
  if (!isSessionStateCurrent(snapshot)) return null
  if (!identity || identity.error || !Number.isSafeInteger(identity.centralUserId)
    || (ReadObject('identity_session')?.centralUserId && identity.centralUserId !== ReadObject('identity_session').centralUserId)
    || !Array.isArray(identity.availableAreas)) throw new Error('Unable to verify access')
  const areas = availableAreaList(identity.availableAreas)
  SaveObject('identity_session', { centralUserId: identity.centralUserId, areas })
  return areas
}

export const SelectEntryArea = async area => {
  const snapshot = captureSessionState()
  const identity = ReadObject('identity_session')
  if (!snapshot.writable || !knownArea(area) || !identity?.areas?.includes(area)) return false
  const response = await POST('/identity/session', { area }, true)
  if (!isSessionStateCurrent(snapshot)) return false
  if (!validSession(response, identity.centralUserId, area)) return false
  SaveObject(IDENTITY_AREA_SESSIONS[area], { ...response, user: normalizeProfileUser(response.user) })
  SaveObject('user', normalizeProfileUser(response.user))
  return passwordChangeRequired(response.user) ? passwordChangePath(area)
    : profileNeedsCompletion(response.user) ? profileCompletionPath(area) : IDENTITY_AREA_ROUTES[area]
}

export const ChangeRequiredPassword = async (area, input) => {
  const snapshot = captureSessionState()
  const identity = ReadObject('identity_session')
  if (!snapshot.writable || !knownArea(area)) return false
  const response = await POST('/identity/password-change', input, true)
  if (!isSessionStateCurrent(snapshot)) return false
  if (!response?.jwt) return response
  if (!validSession(response, identity?.centralUserId, area) || passwordChangeRequired(response.user)) return false
  // Other tokens were restricted to the old credential and must be replaced.
  for (const key of Object.values(IDENTITY_AREA_SESSIONS)) SaveObject(key, {})
  SaveObject(IDENTITY_AREA_SESSIONS[area], { ...response, user: normalizeProfileUser(response.user) })
  SaveObject('user', normalizeProfileUser(response.user))
  return response
}

export const CompleteAreaProfile = async (area, input) => {
  const snapshot = captureSessionState()
  const identity = ReadObject('identity_session')
  if (!snapshot.writable || !knownArea(area)) return false
  const response = await POST('/identity/profile-completion', input, true)
  if (!isSessionStateCurrent(snapshot)) return false
  if (!response?.jwt) return response
  if (!validSession(response, identity?.centralUserId, area) || profileNeedsCompletion(response.user)) return false
  SaveObject(IDENTITY_AREA_SESSIONS[area], { ...response, user: normalizeProfileUser(response.user) })
  SaveObject('user', normalizeProfileUser(response.user))
  return response
}
